import { FormEvent, useEffect, useRef, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { navItems } from '@/mocks/site';

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (searchOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [searchOpen]);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/ranking?q=${encodeURIComponent(value)}` : '/ranking');
    setSearchOpen(false);
    setMenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="flex h-16 w-full items-center gap-3 px-4 md:h-[68px] md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2.5" aria-label="瓜圈速报首页">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-fire-line text-xl" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-xl tracking-wide text-foreground-950 md:text-2xl">瓜圈速报</span>
            <span className="hidden text-[10px] tracking-[0.28em] text-foreground-500 sm:block">HOT GOSSIP</span>
          </span>
        </Link>

        <nav className="ml-6 hidden items-center gap-1 lg:flex" aria-label="主导航">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `rounded-md px-3.5 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          {searchOpen ? (
            <form onSubmit={handleSearch} className="hidden items-center sm:flex" role="search">
              <div className="relative">
                <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400" />
                <input
                  ref={inputRef}
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  type="text"
                  placeholder="搜索热点 / 话题"
                  className="h-10 w-52 rounded-md border border-background-300 bg-background-50 pl-9 pr-3 text-sm text-foreground-900 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-200 lg:w-64"
                />
              </div>
              <button
                type="submit"
                className="ml-2 h-10 cursor-pointer whitespace-nowrap rounded-md bg-foreground-950 px-4 text-sm font-medium text-background-50 transition hover:bg-foreground-800"
              >
                搜索
              </button>
            </form>
          ) : (
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              aria-label="打开搜索"
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-200 text-foreground-700 transition hover:border-primary-300 hover:text-primary-600"
            >
              <i className="ri-search-line text-lg" />
            </button>
          )}

          <Link
            to="/ranking"
            className="hidden h-10 cursor-pointer items-center gap-2 rounded-md bg-primary-500 px-4 text-sm font-medium text-background-50 transition hover:bg-primary-600 md:inline-flex"
          >
            <i className="ri-fire-line" />
            <span className="whitespace-nowrap">查看今日榜单</span>
          </Link>

          <button
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label="打开菜单"
            aria-expanded={menuOpen}
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-200 text-foreground-800 transition hover:border-primary-300 lg:hidden"
          >
            <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-2-line text-xl'} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 lg:hidden">
          <div className="flex flex-col gap-1 px-4 py-4">
            <form onSubmit={handleSearch} className="mb-2 flex items-center sm:hidden" role="search">
              <div className="relative flex-1">
                <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400" />
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  type="text"
                  placeholder="搜索热点 / 话题"
                  className="h-11 w-full rounded-md border border-background-300 bg-background-50 pl-9 pr-3 text-sm outline-none focus:border-primary-400"
                />
              </div>
              <button
                type="submit"
                className="ml-2 h-11 cursor-pointer whitespace-nowrap rounded-md bg-foreground-950 px-4 text-sm font-medium text-background-50"
              >
                搜索
              </button>
            </form>
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `flex items-center justify-between rounded-md px-4 py-3 text-sm font-medium transition-colors ${
                    isActive ? 'bg-primary-100 text-primary-700' : 'text-foreground-800 hover:bg-background-100'
                  }`
                }
              >
                {item.label}
                <i className="ri-arrow-right-s-line text-lg opacity-50" />
              </NavLink>
            ))}
            <Link
              to="/ranking"
              onClick={() => setMenuOpen(false)}
              className="mt-2 flex h-11 items-center justify-center gap-2 rounded-md bg-primary-500 text-sm font-medium text-background-50"
            >
              <i className="ri-fire-line" />
              <span>查看今日榜单</span>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}